import React, { useState, useRef, useEffect } from 'react';
import styles from './style.module.scss';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import { CiUser, CiMail, CiLock } from 'react-icons/ci';
import { BsArrowRight, BsEye, BsEyeSlash, BsShieldCheck, BsQuestionCircle, BsWhatsapp } from 'react-icons/bs';
import { FaGoogle, FaFacebook } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import logo from '../../assets/logo.png';
import { api } from '../../services/apiClient';
import { useSaldo } from '../../contexts/SaldoContext';

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const { fetchSaldo } = useSaldo();
  const containerRef = useRef(null);

  // Form states
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [forgotEmail, setForgotEmail] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  // Validation functions
  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^$$\d{2}$$\s\d{4,5}-\d{4}$/;
    const cpfRegex = /^\d{3}\.\d{3}\.\d{3}-\d{2}$/;
    const cnpjRegex = /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/;
    
    return regex.test(email) || phoneRegex.test(email) || cpfRegex.test(email) || cnpjRegex.test(email) || email.length > 0;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!email.trim()) {
      newErrors.email = 'Campo obrigatório';
    } else if (!validateEmail(email)) {
      newErrors.email = 'Formato inválido';
    }

    if (!password.trim()) {
      newErrors.password = 'Senha é obrigatória';
    } else if (password.length < 6) {
      newErrors.password = 'Senha deve ter pelo menos 6 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);

    try {
      const logged = await login(email, password);
      if (logged) {
        navigate('/');
        fetchSaldo();
        toast.success('Login realizado com sucesso!');
      } else {
        toast.warn('Credenciais inválidas. Verifique seus dados.');
      }
    } catch (error) {
      console.error('Erro ao fazer login:', error);
      toast.error('Erro interno. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    
    if (!forgotEmail.trim()) {
      toast.warn('Digite seu e-mail para recuperação');
      return;
    }

    if (!validateEmail(forgotEmail)) {
      toast.warn('Digite um e-mail válido');
      return;
    }

    setForgotPasswordLoading(true);

    try {
      const response = await api.post('/password/enviar', { email: forgotEmail });
      
      if (response.data.ok) {
        toast.success('E-mail de recuperação enviado com sucesso!');
        setIsModalVisible(false);
        setForgotEmail('');
      } else {
        toast.warn('E-mail não encontrado em nossa base de dados');
      }
    } catch (error) {
      toast.error('Erro ao enviar e-mail de recuperação');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleWhatsAppContact = () => {
    const message = encodeURIComponent('Olá! Preciso de ajuda para recuperar meu acesso à conta.');
    window.open(`https://api.whatsapp.com/send?phone=5534991860561&text=${message}`, '_blank');
  };

  return (
    <div ref={containerRef} className={styles.container}>
      <div className={styles.loginCard}>
        {/* Header */}
        <div className={styles.header}>
          <Link to="/" className={styles.logoContainer}>
            <img src={logo || "/placeholder.svg"} alt='Logo da empresa' className={styles.logo} />
          </Link>
          <h1>Bem-vindo de volta!</h1>
          <p>Faça login para acessar sua conta e participar dos melhores bolões</p>
        </div>

        {/* Login Form */}
        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.inputGroup}>
            <label htmlFor="email">E-mail, Celular, CPF ou CNPJ</label>
            <div className={`${styles.inputContainer} ${errors.email ? styles.error : ''}`}>
              <CiUser className={styles.inputIcon} />
              <input
                id="email"
                type="text"
                placeholder="Digite seu e-mail, celular, CPF ou CNPJ"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={styles.input}
              />
            </div>
            {errors.email && <span className={styles.errorMessage}>{errors.email}</span>}
          </div>

          <div className={styles.inputGroup}>
            <label htmlFor="password">Senha</label>
            <div className={`${styles.inputContainer} ${errors.password ? styles.error : ''}`}>
              <CiLock className={styles.inputIcon} />
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Digite sua senha"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={styles.input}
              />
              <button
                type="button"
                className={styles.passwordToggle}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <BsEyeSlash /> : <BsEye />}
              </button>
            </div>
            {errors.password && <span className={styles.errorMessage}>{errors.password}</span>}
          </div>

          <button
            type="submit"
            className={`${styles.button} ${styles.buttonPrimary}`}
            disabled={loading}
          >
            {loading ? (
              <>
                <div className={styles.spinner}></div>
                Entrando...
              </>
            ) : (
              <>
                Entrar
                <BsArrowRight />
              </>
            )}
          </button>
        </form>

        {/* Divider */}
        <div className={styles.divider}>
          <span>ou</span>
        </div>

        {/* Social Login */}
        {/* <div className={styles.socialLogin}>
          <button className={`${styles.button} ${styles.buttonSocial} ${styles.buttonGoogle}`}>
            <FaGoogle />
            Continuar com Google
          </button>
          <button className={`${styles.button} ${styles.buttonSocial} ${styles.buttonFacebook}`}>
            <FaFacebook />
            Continuar com Facebook
          </button>
        </div> */}

        {/* Action Links */}
        <div className={styles.actionLinks}>
          <div className={styles.linkGroup}>
            <span>Não tem uma conta?</span>
            <Link to='/cadastrar' className={styles.actionLink}>
              <strong>Cadastre-se aqui</strong>
            </Link>
          </div>

          <div className={styles.linkGroup}>
            <span>Esqueceu a senha?</span>
            <button 
              type="button"
              onClick={() => setIsModalVisible(true)}
              className={styles.actionButton}
            >
              <BsShieldCheck />
              Recuperar senha
            </button>
          </div>

          <div className={styles.linkGroup}>
            <span>Precisa de ajuda?</span>
            <button 
              type="button"
              onClick={handleWhatsAppContact}
              className={styles.actionButton}
            >
              <BsWhatsapp />
              Fale conosco
            </button>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {isModalVisible && (
        <div className={styles.modalOverlay} onClick={() => setIsModalVisible(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Recuperação de Senha</h2>
              <button 
                className={styles.modalClose}
                onClick={() => setIsModalVisible(false)}
              >
                ×
              </button>
            </div>

            <div className={styles.modalContent}>
              <div className={styles.modalIcon}>
                <BsShieldCheck />
              </div>
              <p>Digite seu e-mail para receber as instruções de recuperação de senha</p>

              <form onSubmit={handleForgotPassword}>
                <div className={styles.inputGroup}>
                  <label htmlFor="forgotEmail">E-mail</label>
                  <div className={styles.inputContainer}>
                    <CiMail className={styles.inputIcon} />
                    <input
                      id="forgotEmail"
                      type="email"
                      placeholder="Digite seu e-mail"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      className={styles.input}
                      required
                    />
                  </div>
                </div>

                <div className={styles.modalActions}>
                  <button
                    type="button"
                    onClick={() => setIsModalVisible(false)}
                    className={`${styles.button} ${styles.buttonSecondary}`}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className={`${styles.button} ${styles.buttonPrimary}`}
                    disabled={forgotPasswordLoading}
                  >
                    {forgotPasswordLoading ? (
                      <>
                        <div className={styles.spinner}></div>
                        Enviando...
                      </>
                    ) : (
                      <>
                        Enviar
                        <BsArrowRight />
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
